import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm, useWatch } from 'react-hook-form';
import {
    FormContainer,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import {
    BusinessInput,
    EditButtons,
    ImageField,
} from '@barboxx/admin-components';
import { CATEGORY_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Category } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import {
    CATEGORY_TYPES,
    ImageKitCustomMetadataKeys,
    ImageKitFolders,
    ImageKitNamedTransforms,
    ImageKitTypes,
} from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

import { REACT_APP_ENV } from '../../../../../environments/environment';

interface Props {
    category: Category.DetailsResponse;
}

export const CategoryDetailsCard = ({ category }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateUser = useMutation({
        mutationKey: ['category', category.id],
        mutationFn: (data: Category.UpdateRequest) =>
            Api.Categories.Category.putCategory(category.id, data),
    });

    const formContext = useForm<Category.UpdateRequest>({
        resolver: yupResolver(CATEGORY_SCHEMA),
        defaultValues: category,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: Category.UpdateRequest) => {
        mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated product!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating product!', {
                    severity: 'error',
                });
            });
    };

    const values = useWatch(formContext) as Category;

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Category Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <SelectElement
                            label="Category Type"
                            name="categoryType"
                            options={enumToFormOptions(CATEGORY_TYPES)}
                            fullWidth
                            disabled={!editable}
                            onChange={value => {
                                if (value !== CATEGORY_TYPES.Business) {
                                    // Reset businessId when categoryType changes
                                    formContext.setValue('businessId', null);
                                }
                            }}
                        />
                        {values.categoryType === CATEGORY_TYPES.Business && (
                            <BusinessInput />
                        )}
                        <ImageField
                            label="Image"
                            name="image"
                            readOnly={!editable}
                            imagekitUploadProps={{
                                width: 300,
                                fileName: `${values.id}`,
                                folder: ImageKitFolders.Categories,
                                env: REACT_APP_ENV,
                                customMetadata: {
                                    [ImageKitCustomMetadataKeys.Id]: `${values['id']}`,
                                    [ImageKitCustomMetadataKeys.Type]:
                                        ImageKitTypes.Category,
                                    [ImageKitCustomMetadataKeys.Env]:
                                        REACT_APP_ENV,
                                },
                                tags: [
                                    values['name'],
                                    ImageKitTypes.Category,
                                    `${values['id']}`,
                                    REACT_APP_ENV,
                                ],
                            }}
                            imagekitProps={{
                                width: 300,
                                transformation: [
                                    {
                                        raw: ImageKitNamedTransforms.Web.square,
                                    },
                                ],
                                loading: 'lazy',
                                lqip: {
                                    raw: ImageKitNamedTransforms.Web.squareLq,
                                },
                            }}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="Category">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
